import styled from "styled-components";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { ITheme } from "@swiggy-private/dls-web-common/dist/components/types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

interface IRewardsWidgetContainerProps {
    backgroundImage?: string;
    booster?: boolean;
    isScenesFlow?: boolean;
    skipOuterMargin?: boolean;
}
const arrowDimensions = 12;

const getBorder = (theme: ITheme, booster?: boolean, isScenesFlow?: boolean) => {
    if (booster) return "none";
    if (isScenesFlow) return `1px solid ${theme.colors[theme.colorMode].border_Tertiary}`;
    return `3px solid ${theme.colors[theme.colorMode].background_Primary}`;
};

export const RewardsWidgetWithNudgeWrapper = styled.div<{
    booster?: boolean;
    isScenesFlow?: boolean;
}>(({ theme, booster, isScenesFlow }) => ({
    display: "flex",
    flexDirection: "column",
    margin: booster || isScenesFlow ? undefined : `${theme.spacing.large} ${theme.spacing.medium}`,
}));

export const RewardsWidgetContainer = styled.div<IRewardsWidgetContainerProps>(
    ({ theme, backgroundImage, booster, isScenesFlow, skipOuterMargin }) => {
        const shouldApplyOuterMargin = !skipOuterMargin && !booster && !isScenesFlow;

        return {
            display: "flex",
            padding: `${theme.spacing.small} 14px`,
            margin: shouldApplyOuterMargin ? `${theme.spacing.large} ${theme.spacing.medium}` : undefined,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexDirection: "column",
            backgroundRepeat: isScenesFlow ? undefined : "no-repeat",
            borderRadius: theme.cornerRadius.medium,
            backgroundColor: booster ? theme.colors[theme.colorMode].background_Primary : "transparent",
            border: getBorder(theme, booster, isScenesFlow),
            ...(skipOuterMargin
                ? {
                      position: "relative",
                      zIndex: 1,
                  }
                : {}),
        };
    },
);

export const RewardsDineCashContainer = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
}));

export const RewardLogo = styled.div<{ disabled?: boolean }>(({ theme, disabled }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    width: `${theme.spacing.xLarge}`,
    height: `${theme.spacing.xLarge}`,
    marginBottom: `${theme.spacing.xxxSmall}`,
    opacity: disabled ? 0.2 : 1,
}));

export const RewardsTextContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

export const RewardTitleRow = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xxxSmall,
    flexWrap: "wrap",
}));

export const RewardExtraInfoIconTrigger = styled.div(({ theme }) => ({
    display: "inline-flex",
    cursor: "pointer",
    lineHeight: 0,
    borderRadius: theme.cornerRadius.rounded,
    "&:focus-visible": {
        outline: `2px solid ${theme.colors[theme.colorMode].border_Secondary}`,
        outlineOffset: 2,
    },
}));

/** Wraps info trigger + absolutely positioned DLS `ToolTip` above the icon. */
export const RewardExtraInfoAnchor = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
`;

export const REWARD_EXTRA_INFO_DLS_TOOLTIP_CLASS = "reward-extra-info-dls-tooltip";

export const RewardExtraInfoDlsTooltipWrap = styled.div`
    position: absolute;
    left: 50%;
    bottom: calc(100% + 4px);
    transform: translateX(-50%);
    max-width: 190px;
    z-index: 20;

    & .${REWARD_EXTRA_INFO_DLS_TOOLTIP_CLASS} {
        max-width: 190px !important;
        width: max-content !important;
    }
`;

export const RedeemableStyledText = styled(Text)`
    margin-bottom: ${({ theme }) => theme.spacing.xxSmall};
`;

export const RedeemableContainerWithArrow = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xxxSmall};
`;

export const TopArrowIconContainer = styled.div(({ theme }) => ({
    marginBottom: theme.spacing.xxxSmall,
}));

export const UseRewardContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
`;

export const ApplyRewardContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xSmall};
    margin-top: 7px;
    padding: 1px 0;
`;

export const EarnRewardsContainer = styled.div<{ isTitle: boolean }>(({ theme, isTitle }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing.xSmall,
    paddingBottom: isTitle ? theme.spacing.small : 0,
    borderBottom: isTitle ? `1px dotted ${theme.colors[theme.colorMode].border_Tertiary}` : "none",
    marginBottom: isTitle ? theme.spacing.small : 0,
}));

export const EarnedReward = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xxxSmall,
}));

export const ExtraInfoContainer = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.xxxSmall};
`;

export const SubtitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xxxSmall};
`;

export const TooltipContainer = styled.div`
    margin-top: -60px;
    .tooltip-container {
        position: relative;
        background: ${({ theme }) => theme.colors[theme.colorMode].background_Primary};
        padding: ${({ theme }) => theme.spacing.small};
        border-radius: ${({ theme }) => theme.spacing.small};
        max-width: 250px;
        left: 58px;
        bottom: 55px;
        margin-top: ${arrowDimensions / 2}px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .tooltip-container:before {
        //arrow
        content: "";
        width: ${arrowDimensions}px;
        height: ${arrowDimensions}px;
        background-color: ${({ theme }) => theme.colors[theme.colorMode].background_Primary};
        position: absolute;
        bottom: 0px;
        left: 45%;
        transform: translateY(47%) rotate(-45deg);
        border-radius: 0 0 0 ${({ theme }) => theme.cornerRadius.xxSmall};
        clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
    }
`;

export const InfoContainer = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing.xxSmall};
    align-items: center;
    justify-content: center;
`;

export const ToolTipIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: ${({ theme }) => theme.spacing.small};
`;

export const BottomNudgeTextCalloutContainer = styled.div<{
    backgroundColor: keyof IColors;
}>(({ theme, backgroundColor }) => {
    const resolvedBackgroundColor = theme.colors[theme.colorMode][backgroundColor];
    const borderGradient = "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.1) 100%)";
    return {
        paddingLeft: theme.spacing.small,
        borderBottomLeftRadius: theme.spacing.medium,
        borderBottomRightRadius: theme.spacing.medium,
        backgroundColor: resolvedBackgroundColor,
        marginTop: `calc(-1 * ${theme.spacing.large})`,

        zIndex: 0,
        paddingTop: theme.spacing.xLarge,
        position: "relative",
        paddingBottom: theme.spacing.xxSmall,
        backgroundImage: `linear-gradient(${resolvedBackgroundColor}, ${resolvedBackgroundColor}), ${borderGradient}`,
        backgroundOrigin: "border-box",
        backgroundClip: "padding-box, border-box",
        border: "1px solid transparent",
    };
});
